import DOMPurify from 'dompurify';
import React from 'react';

/**
 * Minimal whitelist of tags/attributes permitted in backend- or
 * SurveyJS-sourced rich text. Anything outside this set (scripts, iframes,
 * event-handler attributes, javascript: URIs, etc.) is stripped by DOMPurify.
 */
const ALLOWED_TAGS = [
  'a',
  'b',
  'i',
  'u',
  'em',
  'strong',
  'p',
  'br',
  'span',
  'div',
  'ul',
  'ol',
  'li',
  'sup',
  'sub',
  'small',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6'
];

const ALLOWED_ATTR = ['href', 'target', 'rel', 'class', 'title'];

const sanitize = (dirty: string): string =>
  DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    // Belt-and-suspenders: never allow scriptable content even if a tag
    // above were widened later.
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form'],
    FORBID_ATTR: ['style']
  });

export interface SafeHtmlProps {
  /** Untrusted HTML string (from backend / SurveyJS / config). */
  html?: string | null;
  /** Wrapper element tag. Defaults to an inline `span`. */
  as?: keyof JSX.IntrinsicElements;
  className?: string;
}

/**
 * Safely render an untrusted HTML string. Sanitizes with DOMPurify (minimal
 * whitelist) before injecting via `dangerouslySetInnerHTML`. Drop-in
 * replacement for the previous `react-html-parser` usage, which performed no
 * sanitization and allowed stored/reflected XSS.
 */
const SafeHtml: React.FC<SafeHtmlProps> = ({
  html,
  as: Tag = 'span',
  className
}) => {
  if (!html) {
    return null;
  }

  return (
    <Tag
      className={className}
      dangerouslySetInnerHTML={{ __html: sanitize(html) }}
    />
  );
};

export default SafeHtml;
